import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef, ColGroupDef, DataTypeDefinition } from 'ag-grid-community';
import { throttle } from 'async-toolbox/throttle'
import { AGGridImportRowData, ImportRow } from './resourceImportEditGrid/types';
import { HoursEditor, HoursRenderer } from './resourceImportEditGrid/hoursRenderer';
import { Toaster } from './ui/toaster';
import { useToast } from './hooks/use-toast';
import { QualificationRenderer, QualificationEditor } from './resourceImportEditGrid/qualificationRenderer';
import { ServiceEditor, ServiceRenderer } from './resourceImportEditGrid/serviceRenderer';
import { present } from '@/lib/utils';
import { SkipRowRenderer } from './resourceImportEditGrid/skipRowRenderer';
import { i18n } from "../lib/i18n";

interface ResourceImportEditProps {
  data: {
    "data": ImportRow[]
  }
  qualifications: Array<{ id: number; name: string; }>,
  services: Array<{ id: number; name: string; category: string }>
}


const DataTypeDefinitions: {
  [cellDataType: string]: DataTypeDefinition<AGGridImportRowData>;
} = {
}

export function ResourceImportEditGrid(props: ResourceImportEditProps) {
  const originalData = useMemo(() => props.data, [])
  const originalDataHasIDs = useMemo(() => originalData.data.some((d) => d.parsed.id), [originalData])
  const [parsedData, setParsedData] = useState(props.data.data.map(prepareParsedData))

  const allQualifications = Array.from(new Set([
    ...props.data.data.flatMap((d) => d.parsed.qualifications),
    ...props.qualifications.map((q) => q.name)
  ].filter(present)))

  const allServices = uniqBy(
    [
      ...props.data.data.flatMap((d) => d.parsed.services),
      ...props.services
    ].filter((q) => present(q) && present(q.name) && present(q.category)),
    (a, b) => a.name === b.name && a.category === b.category
  )

  const colDefs: (ColDef<AGGridImportRowData> | ColGroupDef<AGGridImportRowData>)[] = useMemo(() => {
    const cols: (ColDef<AGGridImportRowData> | ColGroupDef<AGGridImportRowData>)[] = [
      {
        field: 'skip',
        headerName: i18n.t('resource_import.skip_this_row'),
        editable: true,
        cellDataType: 'boolean',
        width: 120,
        cellRenderer: SkipRowRenderer,
        comparator: (_valueA, _valueB, nodeA, nodeB, _isDescending) => {
          if (!nodeA.data.skip && nodeB.data.skip) return -1
          if (nodeA.data.skip && !nodeB.data.skip) return 1
          if (nodeA.data.skip_reason === nodeB.data.skip_reason) return 0
          return nodeA.data.skip_reason < nodeB.data.skip_reason ? -1 : 1
        }
      },
      { field: 'active', editable: true, cellDataType: 'boolean', width: 120 },
      { field: 'name', editable: true },
      { field: 'full_address', editable: true, headerName: i18n.t('resource_import.address') },
      { field: 'phone', editable: true },
      { field: 'website', editable: true },
      { field: 'description', editable: true },
      { field: 'hours', editable: true, headerName: i18n.t('resource_import.hours'), cellRenderer: HoursRenderer, cellEditor: HoursEditor },
      {
        field: 'qualifications',
        headerName: i18n.t('resource_import.qualifications'),
        editable: true,
        cellRenderer: QualificationRenderer,
        cellEditor: QualificationEditor,
        cellEditorParams: {
          qualifications: allQualifications
        },
        cellRendererParams: {
          qualifications: allQualifications
        }
      },
      {
        field: 'services',
        headerName: i18n.t('resource_import.services'),
        editable: true,
        cellRenderer: ServiceRenderer,
        cellEditor: ServiceEditor,
        cellEditorParams: {
          services: allServices
        },
        cellRendererParams: {
          services: allServices
        }
      },
    ]

    cols.forEach(col => {
      if (!('children' in col)) {  // Skip column groups if any
        col.cellClass = (params) => {
          return params.data.skip ? 'skipped-row' : '';
        };
      }
    });

    if (originalDataHasIDs) {
      cols.splice(1, 0, { field: 'id', editable: false })
    }

    return cols.filter(present)
  }, [allQualifications, allServices, originalDataHasIDs])

  const [enqueueSync] = useSyncToServer()

  useEffect(() => {

    enqueueSync({
      data: originalData.data.map((row, idx) => {
        return {
          ...row,
          parsed: parsedData[idx]
        }
      })
    })
  }, [parsedData])

  const onCellValueChanged = useCallback((e: CellValueChangedEvent<AGGridImportRowData>) => {
    setParsedData((rowData) => {
      const newRowData = [...rowData]
      newRowData[e.rowIndex] = { ...e.data }
      return newRowData
    })
  }, [])

  return (
    <div className="resource-import-edit-grid ag-theme-quartz" style={{ height: '100%' }}>
      <AgGridReact
        rowData={parsedData}
        columnDefs={colDefs}
        dataTypeDefinitions={DataTypeDefinitions}
        onCellValueChanged={onCellValueChanged}
        getRowClass={(params) => params.data.skip ? 'skipped-row' : ''}
      />

      <Toaster />
    </div>
  )
}

function useSyncToServer() {
  let nextSyncData = useRef<{ data: ImportRow[] }>()

  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { toast } = useToast();

  const syncToServer = useCallback(throttle(async () => {
    if (!nextSyncData.current) {
      return
    }

    try {

      const data = nextSyncData.current
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const resp = await fetch('./', {
        method: 'PUT',
        body: JSON.stringify({ 'resource_import': data }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        }
      })

      if (!resp.ok) {
        throw new Error(`Unexpected response: ${resp.status}`)
      }
    } finally {
      nextSyncData.current = undefined
      setDirty(false)
    }
  }, 5000), [])

  const enqueueSync = useCallback((data: { data: ImportRow[] }) => {
    setDirty(true)
    nextSyncData.current = data
  }, [])

  const syncNow = useCallback((data?: { data: ImportRow[] }) => {
    if (data) {
      setDirty(true)
      nextSyncData.current = data
    }
    syncToServer()
      .then(() => {
        setDirty(false)
      })
      .catch((err) => {
        setDirty(false)
        console.error(err)
        setError(err)
        toast({
          variant: 'destructive',
          title: i18n.t('resource_import.unable_to_save'),
          description: err.message
        })
      })
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (nextSyncData.current) {
        syncNow()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (dirty) {
      document.querySelectorAll('[data-disable-during-sync]').forEach((el) => {
        el.setAttribute('disabled', 'true')
        el.classList.add('disabled')
      })
    } else {
      document.querySelectorAll('[data-disable-during-sync]').forEach((el) => {
        el.removeAttribute('disabled')
        el.classList.remove('disabled')
      })
    }
  }, [dirty])

  return [
    enqueueSync,
    { dirty, error },
    { syncNow }
  ] as const
}


function uniqBy<T>(arr: T[], compare: (a: T, b: T) => boolean) {
  return arr.filter((v, i, self) => self.findIndex((t) => compare(t, v)) === i)
}

function prepareParsedData(original: ImportRow): AGGridImportRowData {
  let shouldSkip = original.parsed.skip ?? false
  let skipReason: string | undefined = original.parsed.skip_reason

  // skip rows where we have no ability to contact the resource
  if (!original.parsed.phone && !original.parsed.website && !original.parsed.full_address) {
    shouldSkip = true
    skipReason = i18n.t('resource_import.missing_data')
  }

  return {
    ...original.parsed,

    active: original.parsed.active ?? (shouldSkip ? false : true),
    // Skip duplicates by default
    skip: shouldSkip,
    skip_reason: skipReason
  }
}

